/**
 * Clear site storage on logout.
 *
 * Programmatically clears the same kinds of data that Chrome's "Clear site data"
 * (Application → Storage) clears, so the next login gets a fresh slate. This helps
 * avoid MFE loading failures caused by stale cached scripts (e.g. remoteEntry.js)
 * or broken remote containers for returning users.
 *
 * Call from the logout flow only. Does not clear cookies (handled by
 * clearCookiesForCurrentOrigin); auth services clear their own localStorage keys.
 *
 * @module utils/clearSiteStorage
 */

/**
 * Clear Cache API caches (Cache Storage).
 * Old MFE chunks and remoteEntry.js are often served from here and can cause load failures.
 */
async function clearCacheStorage(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch {
    // Ignore; some environments restrict Cache API
  }
}

/**
 * Unregister all service workers for this scope.
 * Prevents a caching SW from serving stale MFE assets on next visit.
 */
async function unregisterServiceWorkers(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
  } catch {
    // Ignore
  }
}

/**
 * Clear localStorage and sessionStorage for the current origin.
 * Removes any leftover app/MFE state so the next session starts clean.
 */
function clearWebStorage(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.clear();
  } catch {
    // Ignore (private mode, quota, etc.)
  }
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.clear();
  } catch {
    // Ignore
  }
}

/**
 * Clear site storage for the current origin (Cache API, web storage, service workers).
 * Safe to call on logout; runs asynchronously and never throws.
 * Does not clear cookies — use clearCookiesForCurrentOrigin() in the logout flow as well.
 */
export function clearSiteStorageOnLogout(): void {
  clearWebStorage();
  void clearCacheStorage().then(unregisterServiceWorkers);
}
